/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { SavedViewPopover } from '@/components/ui/saved-views/SavedViewPopover'
import { useSavedViews } from '@/components/ui/saved-views/useSavedViews'
import { useSettledDirty } from '@/components/ui/saved-views/useSettledDirty'

import { type ColdStorageSavedView, useColdStorage } from './ColdStorageContext'

/** What a saved view is compared on; the list width is left out, so dragging the divider isn't a change. */
export const comparable = (view: ColdStorageSavedView) => ({
  companyId: view.companyId ?? null,
  locationIds: view.locationIds ? [...view.locationIds].sort((a, b) => a - b) : null,
  filters: view.filters,
})

/** The cold storage (or blast freezer) saved views, in the profile's savedViews, per screen. */
export const ColdStorageSavedViews = () => {
  const context = useColdStorage()
  const { selectedView, setSelectedView } = context
  const saved = useSavedViews<ColdStorageSavedView>(context.mode.mode)

  const isDirty = useSettledDirty(
    selectedView,
    comparable(context.currentView('', false)),
    context.isSwitchingView || context.isLoading
  )

  const store = (view: ColdStorageSavedView) => {
    saved.store(view)
    setSelectedView(view)
  }

  return (
    <SavedViewPopover
      selected={selectedView}
      viewNames={saved.viewNames}
      isDirty={isDirty}
      onSaveAsNew={(name, isDefault) =>
        saved.storeNew(name, (trimmedName) => {
          const view = context.currentView(trimmedName, isDefault)
          setSelectedView(view)
          return view
        })
      }
      onSaveSelected={() => selectedView && store(context.currentView(selectedView.name, !!selectedView.isDefault))}
      onSetDefault={(isDefault) => selectedView && store({ ...selectedView, isDefault })}
      onDelete={() => {
        if (!selectedView) return
        saved.remove(selectedView.name)
        setSelectedView(null)
      }}
      onSelect={(name) => {
        const view = saved.byName[name]
        if (view) context.applyView(view)
      }}
      onReset={context.reset}
      resetHelp="Shows every location again, clears the search and the filters, closes any details and puts the list back to its starting width. Keeps the company and your saved views."
    />
  )
}
